import type { Route } from "../../../utils/models/Route";
import { haversineMeters, orderedDayStages, StageConnector } from "./stage-connectors";

/**
 * Each route's Start and End — pure helpers for how they're labelled and for
 * picking a new one from places near the route (RouteEndpoints.vue).
 */

/** Accommodation types a route can start or end at (Accommodation::TYPES). */
export const ENDPOINT_ACCOMMODATION_TYPES = ['bnb', 'campground', 'hostel', 'hotel', 'motel'];

/** Amenity types worth starting or ending a walk at — arrivals, food, shelter, supplies. */
export const ENDPOINT_AMENITY_TYPES = [
  'bus_stop', 'transport_station', 'parking', 'town', 'information',
  'coffee', 'pub', 'restaurant', 'fastfood', 'shop', 'post_office', 'pharmacy',
  'water', 'restroom', 'shelter', 'osmcampsite',
];

/** How far either side of a route its nearby places are looked for. */
export const NEARBY_BUFFER_M = 2000;

export interface NearbyPlace {
  id: string | number;
  /** 'Accommodation' or 'Amenity' — the POI's class, as the POI sheet calls it. */
  category: 'Accommodation' | 'Amenity';
  /** The DB type: hotel, bus_stop, pub… */
  type: string;
  name: string;
  /** [lon, lat] */
  coordinates: [number, number];
}

/** "52.21358, -9.90541" — latitude first, as people read coordinates. */
export function formatLatLon(coordinates: number[]): string {
  return `${coordinates[1].toFixed(5)}, ${coordinates[0].toFixed(5)}`;
}

/** What a route end is called: the place it's pinned to, else its address, else its coordinates. */
export function endpointLabel(input: { destinationName?: string | null; address?: string | null; coordinates: number[] }): string {
  return input.destinationName || input.address || formatLatLon(input.coordinates);
}

/** "Bus stop", "B&B", "Campsite" — a POI type as a person would say it. */
export function placeTypeLabel(type: string): string {
  const special: { [type: string]: string } = { bnb: 'B&B', osmcampsite: 'Campsite', fastfood: 'Fast food', coffee: 'Café' };
  if (special[type]) return special[type];
  const words = type.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A rectangle round the route, `bufferM` beyond its furthest points, as the
 * WKT polygon (lon lat order) the accommodations/amenities-by-polygon queries
 * take. Null for a route with no points.
 */
export function searchAreaAround(coordinates: number[][], bufferM: number = NEARBY_BUFFER_M): string | null {
  const box = searchBoxAround(coordinates, bufferM);
  return box ? boxPolygon(box) : null;
}

/** A lon/lat rectangle. */
export interface SearchBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** The rectangle round some points, `bufferM` beyond their furthest — null for none. */
export function searchBoxAround(coordinates: number[][], bufferM: number = NEARBY_BUFFER_M): SearchBox | null {
  if (coordinates.length === 0) return null;
  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;
  for (const [lon, lat] of coordinates) {
    west = Math.min(west, lon);
    east = Math.max(east, lon);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  const dLat = bufferM / 111320;
  const midLat = ((south + north) / 2) * (Math.PI / 180);
  const dLon = bufferM / (111320 * Math.max(Math.cos(midLat), 0.01));
  return { west: west - dLon, south: south - dLat, east: east + dLon, north: north + dLat };
}

/** A rectangle as the WKT polygon (lon lat order) the places-by-polygon queries take. */
export function boxPolygon(box: SearchBox): string {
  const fix = (value: number) => value.toFixed(6);
  const { west: w, south: s, east: e, north: n } = box;
  return `POLYGON((${fix(w)} ${fix(s)}, ${fix(e)} ${fix(s)}, ${fix(e)} ${fix(n)}, ${fix(w)} ${fix(n)}, ${fix(w)} ${fix(s)}))`;
}

/** A rectangle's four quarters. */
export function quarterBox(box: SearchBox): SearchBox[] {
  const midLon = (box.west + box.east) / 2;
  const midLat = (box.south + box.north) / 2;
  return [
    { west: box.west, south: box.south, east: midLon, north: midLat },
    { west: midLon, south: box.south, east: box.east, north: midLat },
    { west: box.west, south: midLat, east: midLon, north: box.north },
    { west: midLon, south: midLat, east: box.east, north: box.north },
  ];
}

/**
 * The same area as searchAreaAround, but as a run of rectangles each covering
 * about `chunkM` of the line — a long or winding route's single rectangle
 * takes in a lot of country nowhere near it.
 */
export function corridorAreas(coordinates: number[][], bufferM: number = NEARBY_BUFFER_M, chunkM: number = 10000): string[] {
  return corridorBoxes(coordinates, bufferM, chunkM).map(boxPolygon);
}

/** corridorAreas as rectangles. */
export function corridorBoxes(coordinates: number[][], bufferM: number = NEARBY_BUFFER_M, chunkM: number = 10000): SearchBox[] {
  const boxes: SearchBox[] = [];
  let chunk: number[][] = [];
  let chunkLength = 0;
  coordinates.forEach((coord, i) => {
    if (i > 0) chunkLength += haversineMeters(coordinates[i - 1], coord);
    chunk.push(coord);
    if (chunkLength >= chunkM) {
      boxes.push(searchBoxAround(chunk, bufferM)!);
      chunk = [coord];
      chunkLength = 0;
    }
  });
  if (chunk.length > 1 || boxes.length === 0) {
    const box = searchBoxAround(chunk, bufferM);
    if (box) boxes.push(box);
  }
  return boxes;
}

/** Lowercase, without accents or apostrophes, so "cafe" finds "Café" and "sportsmans" "Sportsman's". */
function normalise(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/['’]/g, '').toLowerCase();
}

/**
 * Whether two words are the same give or take one slip: a letter missed,
 * added or wrong, or two the wrong way round.
 */
export function withinOneSlip(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) {
    return a.slice(i + 1) === b.slice(i + 1)
      || (a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2));
  }
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/** Words this short are matched exactly — a slip in them matches too much. */
const SLIP_MIN_LENGTH = 4;

/**
 * Whether a place's name or type has every word of `query` in it (an empty
 * query matches all) — each in full or begun, and a longer word with one
 * slip ("vatry" finds "Vartry House").
 */
export function placeMatchesQuery(place: NearbyPlace, query: string): boolean {
  if (!place.name) return false;
  const words = normalise(query.trim()).split(/\s+/).filter(Boolean);
  const haystack = normalise(`${place.name} ${placeTypeLabel(place.type)}`);
  const nameWords = haystack.split(/[^a-z0-9]+/).filter(Boolean);
  return words.every(word => haystack.includes(word) || (word.length >= SLIP_MIN_LENGTH && nameWords.some(nameWord =>
    withinOneSlip(word, nameWord)
    || withinOneSlip(word, nameWord.slice(0, word.length))
    || withinOneSlip(word, nameWord.slice(0, word.length + 1)))));
}

/**
 * The places to offer for a Start or End: those whose name or type matches
 * `query` (all of them for an empty query), nearest to `from` first.
 */
export function rankNearbyPlaces(places: NearbyPlace[], query: string, from: number[], limit: number = 8): NearbyPlace[] {
  return places.filter(place => placeMatchesQuery(place, query))
    .map(place => ({ place, distance: haversineMeters(from, place.coordinates) }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)
    .map(entry => entry.place);
}

/**
 * The marker the map draws at a route's Start or End, so its card can show
 * the same one:
 *  - the green start point where nothing leads into the route,
 *  - a numbered stop where one day hands over to the next, numbered with
 *    the day that STARTS there,
 *  - the small join where two routes of the same day meet,
 *  - the checkered finish anywhere else (the plan's end, or an alternative
 *    or side route's).
 */
export type EndpointMarker =
  | { kind: 'start' }
  | { kind: 'finish' }
  | { kind: 'stage'; number: number }
  | { kind: 'join' };

export function endpointMarker(
  routes: { [routeKey: string]: Route },
  routeKey: string,
  which: 'start' | 'end',
  stageConnectors: StageConnector[] = [],
  routeJoins: StageConnector[] = []
): EndpointMarker {
  const route = routes[routeKey];
  if (!route || (route.route_style ?? 'route') !== 'route') return { kind: which === 'start' ? 'start' : 'finish' };

  if (which === 'start') {
    const flag = stageConnectors.find(connector => connector.nextRouteKey === routeKey);
    if (flag) return { kind: 'stage', number: flag.nextDayNumber };
    if (routeJoins.some(join => join.nextRouteKey === routeKey)) return { kind: 'join' };
    return { kind: 'start' };
  }

  if (routeJoins.some(join => join.prevRouteKey === routeKey)) return { kind: 'join' };
  const stages = orderedDayStages(routes, 1);
  const stageIndex = stages.findIndex(candidate => candidate.lastKey === routeKey);
  const nextStage = stageIndex >= 0 ? stages[stageIndex + 1] : undefined;
  if (nextStage) return { kind: 'stage', number: nextStage.dayNumber };
  return { kind: 'finish' };
}
